import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Loader2, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionModal = ({ open, onOpenChange, user }) => {
  const [loading, setLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('yearly');

  const pricing = {
    monthly: {
      price: 500,
      priceId: process.env.REACT_APP_STRIPE_PRICE_MONTHLY,
      period: 'month'
    },
    yearly: {
      price: 5000,
      priceId: process.env.REACT_APP_STRIPE_PRICE_YEARLY,
      period: 'year',
      discount: 17,
      save: 1000
    }
  };

  // Debug: Log environment variables on mount
  React.useEffect(() => {
    console.log('🔍 Stripe Environment Check:', {
      publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? '✅ SET' : '❌ MISSING',
      monthlyPriceId: process.env.REACT_APP_STRIPE_PRICE_MONTHLY || '❌ MISSING',
      yearlyPriceId: process.env.REACT_APP_STRIPE_PRICE_YEARLY || '❌ MISSING',
      backendUrl: process.env.REACT_APP_BACKEND_URL || '❌ MISSING'
    });
  }, []);

  const features = [
    'Unlimited daily MCQ challenges',
    'Access to all subjects (Physics, Chemistry, Biology)',
    'Detailed performance analytics',
    'Real-time leaderboard rankings',
    'Topic-wise practice questions',
    'Past paper questions database'
  ];

  const handleSubscribe = async () => {
    const priceId = pricing[billingCycle].priceId;
    
    // Validate price ID exists
    if (!priceId) {
      console.error('Price ID missing for billing cycle:', billingCycle);
      toast.error('Subscription configuration error. Please contact support.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      console.log('Creating checkout session with price_id:', priceId);
      
      const response = await axios.post(
        `${API}/subscription/create-checkout-session`,
        { price_id: priceId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  const currentPrice = pricing[billingCycle].price;
  const pricePerMonth = billingCycle === 'yearly' ? Math.round(currentPrice / 12) : currentPrice;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center">Subscribe to Premium</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`relative px-6 py-2 rounded-lg font-medium transition-all ${
                billingCycle === 'yearly'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Yearly
              <Badge className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2">
                Save 17%
              </Badge>
            </button>
          </div>

          {/* Price Display */}
          <div className="text-center space-y-2">
            <div className="flex items-baseline justify-center gap-2">
              <span className="text-4xl font-bold">LKR {currentPrice}</span>
              <span className="text-muted-foreground">/ {pricing[billingCycle].period}</span>
            </div>
            {billingCycle === 'yearly' && (
              <p className="text-sm text-muted-foreground">
                LKR {pricePerMonth}/month • Save LKR {pricing.yearly.save} per year
              </p>
            )}
            {!user?.trial_used && (
              <Badge variant="outline" className="mt-2">
                <Zap className="w-3 h-3 mr-1" />
                7-Day Free Trial
              </Badge>
            )}
          </div>

          {/* Features */}
          <div className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">{feature}</span>
              </div>
            ))}
          </div>

          {/* Subscribe Button */}
          <Button
            onClick={handleSubscribe}
            disabled={loading}
            className="w-full"
            size="lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              user?.trial_used ? 'Subscribe Now' : 'Start Free Trial'
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Cancel anytime before trial ends. No charge if you cancel during trial.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionModal;
